import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { typeIcons } from '../components/Utils';
import Table from '../components/Table';
import Modal from '../components/Modal';
import axios from '../axiosConfig';
import { useUserPermissions } from '../hooks/useUserPermissions';
import PageTitle from "../components/PageTitle";

const ELearning = () => {
  const navigate = useNavigate();
  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', duration: '', price: '' });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (permissionsLoading) return;
    if (!permissions?.can_view_elearning) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchCourses();
  }, [permissionsLoading, permissions?.can_view_elearning]);

  if (permissionsLoading) return null;

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/courses/', { withCredentials: true });
      setCourses(res.data || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const emptyForm = () => ({ title: '', description: '', duration: '', price: '' });

  const validate = (data) => {
    const errors = {};
    if (!String(data.title || '').trim()) errors.title = true;
    return errors;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitAttempted) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const buildPayload = (data) => ({
    title: data.title.trim(),
    description: data.description || null,
    duration: data.duration !== '' ? Number(data.duration) : null,
    price: data.price !== '' ? Number(data.price) : null,
  });

  const openCreateModal = () => {
    setFormData(emptyForm());
    setValidationErrors({});
    setSubmitAttempted(false);
    setShowCreateModal(true);
  };

  const openEditModal = (course) => {
    setFormData({
      title: course.title || '',
      description: course.description || '',
      duration: course.duration != null ? String(course.duration) : '',
      price: course.price != null ? String(course.price) : '',
    });
    setValidationErrors({});
    setSubmitAttempted(false);
    setEditingCourse(course);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const errors = validate(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    try {
      await axios.post('/courses/', buildPayload(formData), { withCredentials: true });
      setShowCreateModal(false);
      fetchCourses();
    } catch (err) {
      console.error('Failed to create course:', err);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const errors = validate(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    try {
      await axios.patch(`/courses/${editingCourse.id}/`, buildPayload(formData), { withCredentials: true });
      setEditingCourse(null);
      fetchCourses();
    } catch (err) {
      console.error('Failed to update course:', err);
    }
  };

  const handleDelete = async () => {
    if (!editingCourse) return;
    try {
      await axios.delete(`/courses/${editingCourse.id}/`, { withCredentials: true });
      setEditingCourse(null);
      fetchCourses();
    } catch (err) {
      console.error('Failed to delete course:', err);
    }
  };

  const columns = [
    {
      key: 'title',
      header: 'Title',
      type: 'alphabetical',
      width: 144,
    },
    {
      key: 'description',
      header: 'Description',
      type: 'text',
      truncate: 200,
      width: 240,
    },
    {
      key: 'duration',
      header: 'Duration (min)',
      type: 'numeric',
      width: 96,
      render: (value) => (value != null ? value : '—'),
    },
    {
      key: 'price',
      header: 'Price (GBP)',
      type: 'numeric',
      width: 96,
      render: (value) => (value != null ? `£${Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'),
    },
  ];

  const courseForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="form">
      <label>
        Title: <em>(required)</em>
        <input autoComplete="off"
          type="text"
          name="title"
          value={formData.title}
          onChange={handleFormChange}
          className={validationErrors.title ? 'required-invalid' : ''}
        />
      </label>

      <label>
        Description:
        <textarea autoComplete="off"
          name="description"
          value={formData.description}
          onChange={handleFormChange}
          rows={3}
        />
      </label>

      <label>
        Duration (minutes):
        <input autoComplete="off"
          type="number"
          name="duration"
          value={formData.duration}
          onChange={handleFormChange}
          min="0"
        />
      </label>

      <label>
        Price (GBP):
        <input autoComplete="off"
          type="number"
          name="price"
          value={formData.price}
          onChange={handleFormChange}
          min="0"
          step="0.01"
        />
      </label>

      <div className="modal-footer">
        <button type="submit" className="submit-btn">{submitLabel}</button>
        {editingCourse && (
          <button type="button" className="delete-btn" onClick={handleDelete}>
            Delete
          </button>
        )}
      </div>
    </form>
  );

  return (
    <div className="page-layout">
      <PageTitle>{typeIcons.elearning} eLearning</PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button onClick={openCreateModal}>+ New Course</button>
        </div>
      </div>

      <div className="page-content">
        <Table
          data={courses}
          columns={columns}
          loading={loading}
          emptyMessage="No courses found."
          onRowClick={(course) => openEditModal(course)}
          defaultSortColumn="title"
          defaultSortDirection="asc"
        />
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setValidationErrors({}); }}
        title="New Course"
      >
        {courseForm(handleCreateSubmit, 'Create')}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingCourse}
        onClose={() => { setEditingCourse(null); setValidationErrors({}); }}
        title="Edit Course"
      >
        {editingCourse && courseForm(handleEditSubmit, 'Save Changes')}
      </Modal>
    </div>
  );
};

export default ELearning;
